import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useEffect, useCallback } from 'react';

import useModal from '@/shared/hooks/useModal';
import { useLoadingOverlayStore } from '@/shared/store/loadingOverlayStore';
import type { AxiosError } from 'axios';
import { getApiErrorMessage, isCanceledError } from '@/shared/utils/queryUtils';

interface UseRetryableQueryOptions<TData = unknown, TError extends AxiosError = AxiosError> extends UseQueryOptions<TData, TError> {
  successHandler?: (data: TData) => void;
  failHandler?: (error: TError) => void;
  retryMessage?: string;
  withLoadingOverlay?: boolean;
  /** false를 반환하면 기본 재시도 모달을 띄우지 않는다 (호출부가 failHandler에서 직접 처리). 생략 시 항상 띄운다 */
  shouldShowRetryModal?: (error: TError) => boolean;
}

export const useRetryableQuery = <TData = unknown, TError extends AxiosError = AxiosError>({
  successHandler,
  failHandler,
  retryMessage,
  withLoadingOverlay = true,
  shouldShowRetryModal,
  ...options
}: UseRetryableQueryOptions<TData, TError>) => {
  const setIsLoading = useLoadingOverlayStore((state) => state.setIsLoading);
  const modal = useModal();

  const { data, isLoading, isSuccess, isError, error, refetch, ...rest } = useQuery<TData, TError>({
    ...options,
  });

  const handleRetry = useCallback(async () => {
    if (isError && error) {
      if (isCanceledError(error)) return;
      if (error.response?.status === 401) throw error;

      console.error(`\n┏━━━ ⚠️ ⚠️ error ⚠️ ⚠️ ━━━\n`, error, `\n┗━━━━━━ ⚠️ ⚠️ ⚠️ ⚠️ ⚠️ ━━━━━━━━━\n`);

      failHandler?.(error);
      if (shouldShowRetryModal && !shouldShowRetryModal(error)) return;
      const apiMessage = getApiErrorMessage(error);
      const defaultMessage = apiMessage
        ? `${apiMessage}\n\n다시 시도하시겠습니까?`
        : '데이터를 불러오는 데 실패했습니다.\n문제가 지속되면 관리자에게 문의해 주세요.\n\n다시 시도하시겠습니까?';
      const isConfirmed = await modal.confirm(retryMessage ?? defaultMessage);
      if (isConfirmed) refetch();
    }
  }, [isError, error, failHandler, shouldShowRetryModal, modal, retryMessage, refetch]);

  const handleSuccess = useCallback(() => {
    if (isSuccess && data !== undefined) {
      successHandler?.(data);
    }
  }, [isSuccess, data, successHandler]);

  useEffect(() => {
    handleRetry();
  }, [handleRetry]);

  useEffect(() => {
    handleSuccess();
  }, [handleSuccess]);

  useEffect(() => {
    if (withLoadingOverlay) setIsLoading(isLoading);
    if (withLoadingOverlay && (isError || isSuccess)) setIsLoading(false);
  }, [isLoading, isError, isSuccess, withLoadingOverlay]);

  useEffect(() => {
    return () => {
      if (withLoadingOverlay) setIsLoading(false);
    };
  }, [withLoadingOverlay]);

  return { data, isLoading, isSuccess, isError, error, refetch, ...rest };
};
